import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import DrawerItem from '~/components/DrawerItem'
import Icon from '~/components/Icon'

const DrawerItemPage: Component = () => {
  return (
    <>
      <Title>DrawerItem - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">DrawerItem</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Átomos de navegación para paneles deslizantes. DrawerItem es el componente fundamental para construir menús jerárquicos, con soporte para iconos, indicadores de estado y sub-navegación
            expandible.
          </p>
        </div>

        <div class="space-y-12">
          {/* Anatomía y Estados */}
          <Card>
            <Card.Header
              title="Anatomía del Registro"
              subtitle="Estructura flexible que admite iconos, títulos, subtítulos y elementos finales personalizados."
              icon={<Icon icon="material-symbols-light:widgets-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-8 bg-surface-muted rounded-xl border border-border">
                <div class="space-y-4">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-primary-500">Composición Base</h4>
                  <div class="bg-surface rounded-xl shadow-sm border border-border overflow-hidden divide-y divide-border">
                    <DrawerItem icon="material-symbols-light:home-outline" title="Dashboard" active />
                    <DrawerItem icon="material-symbols-light:person-outline" title="Mi Cuenta" subtitle="Gestiona tu perfil" />
                    <DrawerItem icon="material-symbols-light:lock-outline" title="Seguridad" disabled />
                  </div>
                </div>

                <div class="space-y-4">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-secondary-500">Elementos Finales (End)</h4>
                  <div class="bg-surface rounded-xl shadow-sm border border-border overflow-hidden divide-y divide-border">
                    <DrawerItem title="Suscripción" end={<Badge color="success">Activo</Badge>} />
                    <DrawerItem title="Chat en vivo" end={<Icon icon="material-symbols-light:chat-outline" class="text-primary-500" />} />
                    <DrawerItem title="Reportes" end={<Icon icon="material-symbols-light:chevron-right" size={16} class="opacity-30" />} />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Gestión de Badges y Alertas */}
          <Card>
            <Card.Header
              title="Notificaciones e Indicadores"
              subtitle="Atrae la atención del usuario hacia secciones específicas mediante badges numéricos o puntos de estado."
              icon={<Icon icon="material-symbols-light:notifications-active-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="p-8 bg-surface rounded-xl border border-border shadow-sm max-w-2xl mx-auto divide-y divide-border">
                <DrawerItem icon="material-symbols-light:mail-outline" title="Bandeja de Entrada" badge={{ count: 24, color: 'primary' }} />
                <DrawerItem icon="material-symbols-light:warning-outline" title="Alertas Críticas" badge={{ count: 3, color: 'danger' }} />
                <DrawerItem icon="material-symbols-light:refresh" title="Sincronización" badge={{ dot: true, color: 'info' }} />
                <DrawerItem icon="material-symbols-light:star-outline" title="Favoritos" badge={{ count: 99, max: 50, color: 'warning' }} />
              </div>
            </Card.Body>
          </Card>

          {/* Submenús y Jerarquía */}
          <Card>
            <Card.Header
              title="Jerarquía Expandible"
              subtitle="Crea sistemas de navegación profundos sin saturar la interfaz principal."
              icon={<Icon icon="material-symbols-light:more-vert" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
                <div class="p-1 origin-top transform-gpu">
                  <div class="bg-surface rounded-2xl border-2 border-border shadow-xl overflow-hidden p-2">
                    <DrawerItem
                      icon="material-symbols-light:folder-outline"
                      title="Proyectos"
                      subtitle="14 activos actualmente"
                      defaultOpen
                      submenu={[
                        { label: 'Web Design', to: '/p/web', icon: 'material-symbols-light:movie-outline' },
                        { label: 'Mobile App', to: '/p/app', badge: { count: 3, color: 'secondary' } },
                        { label: 'Branding', to: '/p/brand', badge: { dot: true, color: 'success' } }
                      ]}
                    />
                  </div>
                </div>

                <div class="space-y-6">
                  <h3 class="text-2xl font-black text-foreground italic tracking-tighter">NAVEGACIÓN ESTRUCTURADA</h3>
                  <p class="text-foreground-subtle leading-relaxed">
                    La propiedad <code class="text-primary-600 bg-primary-50 px-1 rounded">submenu</code> acepta un array de objetos <code class="text-xs">DrawerSubItemType</code>, gestionando
                    automáticamente la animación de colapso y los estados de indentación.
                  </p>
                  <div class="flex gap-2">
                    <Badge variant="soft" color="info">
                      Responsive
                    </Badge>
                    <Badge variant="soft" color="secondary">
                      Accessible ARIA
                    </Badge>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Configuración técnica del componente DrawerItem."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">title / subtitle</td>
                      <td class="px-6 py-4 italic text-xs">string | JSX.Element</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Contenido textual principal y secundario.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">icon</td>
                      <td class="px-6 py-4 italic text-xs">string | IconComponent</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Icono decorativo alineado a la izquierda.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">badge</td>
                      <td class="px-6 py-4 italic text-xs">number | DrawerItemBadgePropsType</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-balance">Configuración del indicador de estado/contador.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">submenu</td>
                      <td class="px-6 py-4 italic text-xs">DrawerSubItemType[]</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-balance">Lista de enlaces anidados bajo este item.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">to / href</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Destino de navegación (interno o externo).</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default DrawerItemPage
